// The ecology's plants, drawn: instanced trees, shrubs, ferns and grass tufts on the ground clipmap (server: simulation/src/ecoartist.py makes the plants, ecowin.py sends them; wire: ecoWire.ts).
//
// Human-facing only: the quadruped never sees any of this and nothing here feeds back to the simulation. Every plant is a fact of the painted ecology (its place, size and leaf mass come from the
// server); what is drawn from it (how a crown is built of leaf sprigs, the colour of a leaf, how a frond arches) is only a way of showing it, and varies by a hash of the plant's own place, so the
// same plant looks the same every time.
//
// The look is the client's existing one, not a new one (frozenTrees.ts, palmAssets.ts, grass.ts), so a plant from the ecology and a plant from the old per-plant trees read alike:
//   * trees   a bark-textured trunk (the CC0 bark of palmAssets.ts, its colour over its mean so the plant's own brown still sets the hue, and its height as a bump) under a crown of leaf sprigs;
//   * shrubs  the same sprigs, low and dense, with no trunk;
//   * sprigs  three (two, in the far band) crossed alpha-tested cards carrying frozenTrees.ts's needle spray, scattered through the crown ellipsoid and pointing out of it (ending inside it), lit with the same
//             up-biased normal (bright tops, dark undersides); the distance drops sprigs and enlarges the rest so a crown keeps its weight (the old SPRIG_LOD, here per plant, in the shader);
//   * ferns   fronds of the CC0 pinnate frond sheet of palmAssets.ts, one frond a strip that arches and hangs its leaflets either side of the rib;
//   * grass   grass.ts's blades and colours.
// There is no palm form in the ecology (ecoartist.py), so no plant is drawn as a palm.
//
// One mesh per window and form part: an instanced geometry whose instance attributes are the window's arrays (ecoWire.ts: loc, size, misc); a tree has two (trunk, crown) that share them.
// Everything else is done in the vertex shader:
//   * where it stands: the window's base (a uniform, in doubles minus the floating origin O on the CPU) plus the plant's offset gives the plant's position relative to O, small and exact; the height
//     is the ground clipmap's (groundYRel, the surface that is drawn), so what grows stands on what is seen. The mesh sits at O, so three's matrices are exact too;
//   * its shape: sizes are the plant's own (height, crown width, trunk or tuft width);
//   * its distance: a plant shrinks to nothing over the last quarter of its band's radius (bands are by size, so what is small is drawn only near the eye);
//   * wind: the shared wind field (wind.ts), more on what is tall.
// A plant on ground at or below the sea is not drawn (the ecology file is by cells, the coast is finer), nor before the ground has arrived.
//
// Cost: the ground lookup is per vertex, as the grass does it, but a sprig the distance has dropped leaves the shader before the lookup. A crown is 8 to 22 sprigs of 8 or 12 vertices.
// `setQuality` draws the first share of each window's plants, which is an even thinning (ecoWire.ts strideFor).
import * as THREE from 'three';
import { WindField, WIND_GLSL } from './wind';
import { GROUND_GLSL, type GroundClip } from './groundClip';
import type { Origin } from './origin';
import { makeNeedleTexture, makePalmTextures, BARK_FRAG_DECL, BARK_COLOR, BARK_NORMAL, type PalmTexUniforms } from './frozenTrees';
import { EcoStore, ECO_STREAMS, FORM_TREE, FORM_SHRUB, FORM_FERN, FORM_GRASS, type EcoWindow } from './ecoWire';

const FADE_FROM = 0.72;           // a plant starts to shrink at this share of its band's radius and is gone at the radius
const CROWN_FRAC = [0.55, 0.85];  // a tree's and a shrub's crown height over its height
const SEA_MARGIN_M = 0.25;        // ground this far above the sea or less shows no plants
const ALPHA_CUT = 0.42;           // as the old sprigs: a leaf card shows where its alpha is above this near the eye,
const ALPHA_CUT_FAR = 0.10;       // and above this where the texture is minified to 8 texels to a pixel or more (its mips average the alpha down, so a fixed cut would make a far crown vanish)
const BARK_V_PER_C = 0.9;         // bark tiles per trunk circumference of height (frozenTrees.ts uses 2 for palms; fewer is a longer-grained bark, which trees of a wet forest have)
const BARK_BUMP_SCALE = 0.35;     // the palm bark's relief (frozenTrees.ts) is cut to this share on trees: it is the relief of a palm's rings, too strong for a wet forest's trunks
const SPRAY_M = 0.5;              // a sprig card shows one needle spray per this many metres of its length (a bigger card repeats the spray)
const SPRIG_LOD_M = 0.06;         // a crown keeps its sprigs while its width is at least this share of its distance (the share kept falls in proportion beyond)
const SPRIG_LOD_MIN = 0.15;       // and never fewer than this share
const CROWN_SPRIGS = [8, 16, 22, 16]; // sprigs of a tree's crown by band
const SHRUB_SPRIGS = [8, 12, 16, 12]; // and of a shrub's
const SPRIG_CARDS = [3, 3, 2, 2];     // crossed cards in a sprig by band
const SPRIG_ANCHOR = [0.05, 0.30] as const;  // a sprig starts this far out along its direction in the crown ellipsoid (share of the semi-axes: the first number plus the second times the sprig's depth rank)
const SPRIG_REACH = 0.95;          // and runs this share of the way to the ellipsoid's surface at most
const FERN_FRONDS = 5;
const FERN_SEGS = 4;
// A grass tuft is GRASS_BLADES blades, of which it draws as many as its leaf holds: the leaf area the server sends (leaf kg x 1000 / LMA, LMA 50 g/m2 for grass: ecology.json traits) over the area of one blade.
// A blade has its rank in its azimuth (aBlade.x = azimuth + 8 x its index), so the blades a tuft keeps are an even spread round it whatever their number.
const GRASS_BLADES = 28;
const GRASS_SEGS = 2;
const GRASS_M2_PER_KG = 20.0;      // 1000 / 50
const GRASS_BLADE_W = [0.022, 0.05] as const; // a blade's width at the foot: the first number plus the second times the tuft's width in metres
const GRASS_WIDEN_MAX = 2.0;       // blades are widened up to this much to hold the leaf the thin ones (or the ones the distance drops) cannot
const GRASS_LOD_M = 14.0;          // beyond this distance a tuft keeps fewer blades in proportion (never below GRASS_LOD_MIN of them) and widens the rest
const GRASS_LOD_MIN = 0.3;
const GRASS_STRAW = [0.82, 0.97] as const; // the share of tufts that are straw: hash above the first number, fully straw above the second (the wire has no moisture, so colour is by hash)
const PART_TRUNK = 0, PART_CROWN = 1, PART_FERN = 2, PART_GRASS = 3;

// ---- template geometries ------------------------------------------------------------------------------------------------------------------------------------------

function withAttrs(pos: number[], nor: number[] | null, idx: number[], bl: number[] | null, uv: number[] | null): THREE.BufferGeometry {
  const g = new THREE.BufferGeometry();
  const n = pos.length / 3;
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('normal', new THREE.Float32BufferAttribute(nor ?? new Float32Array(3 * n), 3));    // must exist (else flat shading); the shader replaces it (a card's own is its facing)
  g.setAttribute('aBlade', new THREE.Float32BufferAttribute(bl ?? new Float32Array(4 * n), 4));
  if (uv) g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  g.setIndex(idx);
  return g;
}

/** A unit tapered tube: radius 0.5 at y = 0, `top` times that at y = 1, `sides` sides, open at the ends. */
function trunkGeometry(sides: number, top: number): THREE.BufferGeometry {
  const pos: number[] = [], idx: number[] = [];
  for (let r = 0; r < 2; r++) for (let i = 0; i < sides; i++) {
    const a = (i / sides) * Math.PI * 2, rad = r === 0 ? 0.5 : 0.5 * top;
    pos.push(Math.cos(a) * rad, r, Math.sin(a) * rad);
  }
  for (let i = 0; i < sides; i++) { const j = (i + 1) % sides; idx.push(i, j, sides + i, j, sides + j, sides + i); }
  return withAttrs(pos, null, idx, null, null);
}

/** A crown: `k` sprigs of `cards` crossed cards each (card: x across +-0.5, y along 0..1, as frozenTrees.ts's), every vertex carrying its sprig's numbers in aBlade = (z, azimuth, depth, rank):
 *  the sprig's direction (z of the unit sphere and the angle round it) comes from a golden-ratio walk, so any first run of sprigs is spread evenly over the crown (the distance keeps the first
 *  share, by rank, and the crown thins evenly). */
function crownGeometry(k: number, cards: number): THREE.BufferGeometry {
  const pos: number[] = [], nor: number[] = [], uv: number[] = [], bl: number[] = [], idx: number[] = [];
  const frac = (x: number): number => x - Math.floor(x);
  for (let i = 0; i < k; i++) {
    const z = 2 * frac((i + 0.5) * 0.6180339887) - 1, ph = (i * 2.3999632) % (Math.PI * 2), depth = frac((i + 0.5) * 0.7548776662), rank = (i + 0.5) / k;
    for (let p = 0; p < cards; p++) {
      const a = (p / cards) * Math.PI, cx = Math.cos(a), sz = Math.sin(a);
      const o = pos.length / 3;
      for (const [u, v] of [[-0.5, 0], [0.5, 0], [0.5, 1], [-0.5, 1]] as const) {
        pos.push(cx * u, v, sz * u); nor.push(-sz, 0, cx); uv.push(u + 0.5, v); bl.push(z, ph, depth, rank);
      }
      idx.push(o, o + 1, o + 2, o, o + 2, o + 3);
    }
  }
  return withAttrs(pos, nor, idx, bl, uv);
}

/** Fronds for ferns: each a strip of `segs` rows by three columns (position.x = -1, 0, 1 across the rib, position.y = 0..1 along it); aBlade = (azimuth, -, root offset share, length scale). */
function frondGeometry(fronds: number, segs: number): THREE.BufferGeometry {
  const pos: number[] = [], bl: number[] = [], idx: number[] = [], uv: number[] = [];
  for (let f = 0; f < fronds; f++) {
    const az = (f / fronds) * Math.PI * 2 + (f % 2) * 0.2, sc = 0.8 + 0.4 * ((f * 5) % 7) / 6;
    const o = pos.length / 3;
    for (let s = 0; s <= segs; s++) for (const x of [-1, 0, 1]) { pos.push(x, s / segs, 0); bl.push(az, 0, 0.06, sc); uv.push(Math.abs(x), s / segs); }
    for (let s = 0; s < segs; s++) for (let c = 0; c < 2; c++) {
      const i = o + s * 3 + c;
      idx.push(i, i + 1, i + 3, i + 1, i + 4, i + 3);
    }
  }
  return withAttrs(pos, null, idx, bl, uv);
}

/** Ribbons for grass: each blade a strip of `segs` quads along y in [0, 1]; aBlade = (azimuth, lean, root offset share, length scale). */
function ribbonGeometry(blades: [number, number, number, number][], segs: number): THREE.BufferGeometry {
  const pos: number[] = [], bl: number[] = [], idx: number[] = [];
  for (const b of blades) {
    const o = pos.length / 3;
    for (let s = 0; s <= segs; s++) for (const x of [-0.5, 0.5]) { pos.push(x, s / segs, 0); bl.push(b[0], b[1], b[2], b[3]); }
    for (let s = 0; s < segs; s++) { const i = o + s * 2; idx.push(i, i + 1, i + 2, i + 1, i + 3, i + 2); }
  }
  return withAttrs(pos, null, idx, bl, null);
}

function grassBlades(): [number, number, number, number][] {
  const out: [number, number, number, number][] = [];
  const N = GRASS_BLADES;
  const fr = (v: number): number => v - Math.floor(v);
  // golden-angle azimuths, so any first n blades are spread round the tuft; lean, root offset and length are scattered by index
  for (let i = 0; i < N; i++) out.push([fr(i * 0.6180339887) * Math.PI * 2 + 8 * i, 0.30 + 0.70 * fr(i * 0.7548776662 + 0.2), 0.10 + 0.55 * fr(i * 0.5698402910 + 0.4), 0.70 + 0.60 * fr(i * 0.8191725134 + 0.1)]);
  return out;
}

// ---- shader ------------------------------------------------------------------------------------------------------------------------------------------------------

const DECL = /* glsl */`
${GROUND_GLSL}
attribute vec2 aLoc;
attribute vec4 aSz;       // height, crown width, trunk or tuft width, leaf mass
attribute vec2 aMisc;     // facing, patch bin
attribute vec4 aBlade;
uniform vec2  uBaseRel;   // the window's base minus O
uniform vec3  uEye;       // the eye minus O (x, y, z)
uniform float uFadeR;     // the band's radius
uniform float uCrown;     // crown height over height (trees and shrubs)
varying vec3 vTreeCol;
#if ECO_PART == 0
varying float vBark; varying vec2 vBarkDir; varying float vBarkV;
#elif ECO_PART == 1 || ECO_PART == 2
varying vec2 vNeedleUv; varying vec3 vPalmUv;
#endif

float ehash(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}
` + WIND_GLSL;

interface MatShared { needle: THREE.Texture; palm: PalmTexUniforms }

function makeMaterial(part: number, shrub: boolean, k: number, base: THREE.Vector2, eye: THREE.Vector3, ground: GroundClip, wind: WindField, fadeR: number, sh0: MatShared): THREE.MeshStandardMaterial {
  const u = {
    uBaseRel: { value: base },
    uEye: { value: eye },
    uFadeR: { value: fadeR },
    uCrown: { value: shrub ? CROWN_FRAC[1]! : CROWN_FRAC[0]! },
  };
  const m = new THREE.MeshStandardMaterial({ roughness: part === PART_TRUNK ? 0.92 : 0.85, metalness: 0.0, side: part === PART_TRUNK ? THREE.FrontSide : THREE.DoubleSide, alphaTest: 0 });   // the leaf cards test their own alpha (ALPHA_CUT, lowered with distance)
  m.customProgramCacheKey = () => `ecoplants-v4-${part}-${shrub ? 1 : 0}-${k}`;
  m.onBeforeCompile = (sh) => {
    Object.assign(sh.uniforms, u, ground.uniforms, wind.uniforms);
    if (part === PART_TRUNK) Object.assign(sh.uniforms, { uBark: sh0.palm.uBark, uBarkMean: sh0.palm.uBarkMean, uBarkReady: sh0.palm.uBarkReady });
    if (part === PART_CROWN || part === PART_FERN) Object.assign(sh.uniforms, { uNeedle: { value: sh0.needle }, uPalmLeaf: sh0.palm.uPalmLeaf, uPalmMean: sh0.palm.uPalmMean, uPalmReady: sh0.palm.uPalmReady });
    sh.vertexShader = sh.vertexShader
      .replace('#include <common>', `#include <common>\n#define ECO_PART ${part}\n#define ECO_K ${k}\n#define ECO_SHRUB ${shrub ? 1 : 0}\n` + DECL)
      .replace('#include <beginnormal_vertex>', /* glsl */`
        // --- where this plant is, relative to O, and what the ground is there ---
        vec2 erel = uBaseRel + aLoc;
        float edist = length(erel - uEye.xz);
        float efade = 1.0 - smoothstep(uFadeR * ${FADE_FROM.toFixed(2)}, uFadeR, edist);
        float elod = 1.0;
        #if ECO_PART == 1
          // distance: the crown keeps the first share of its sprigs (by rank) and the rest are larger, so it keeps its weight; a dropped sprig leaves before the ground lookup
          elod = clamp(aSz.y / (max(edist, 1.0) * ${SPRIG_LOD_M.toFixed(3)}), ${SPRIG_LOD_MIN.toFixed(2)}, 1.0);
          if (aBlade.w > elod) efade = 0.0;
        #endif
        #if ECO_PART == 3
          // grass: the tuft keeps as many blades as its leaf area needs (and the distance allows); a dropped blade leaves before the ground lookup, the others widen to hold the leaf
          float grk = floor(aBlade.x / 8.0);
          float gaz = aBlade.x - 8.0 * grk;
          float gw0 = ${GRASS_BLADE_W[0].toFixed(3)} + ${GRASS_BLADE_W[1].toFixed(3)} * aSz.y;
          float gA = max(aSz.x * 0.95 * 0.55 * gw0, 1.0e-5);
          float gLeaf = aSz.w * ${GRASS_M2_PER_KG.toFixed(1)};
          float gkeep = clamp(gLeaf / gA, 4.0, ${GRASS_BLADES.toFixed(1)}) * clamp(${GRASS_LOD_M.toFixed(1)} / max(edist, 1.0), ${GRASS_LOD_MIN.toFixed(2)}, 1.0);
          if (grk + 0.5 > gkeep) efade = 0.0;
          float gwide = clamp(gLeaf / (gkeep * gA), 1.0, ${GRASS_WIDEN_MAX.toFixed(1)});
        #endif
        float eground = -1.0e4;
        float evis = 0.0;
        if (efade > 0.0 && uGTop >= 0) {
          eground = groundYRel(erel);
          evis = step(uGPar.w + ${SEA_MARGIN_M.toFixed(2)}, eground);
        }
        float eh = aSz.x, ecw = aSz.y, edia = aSz.z;
        float eseed = ehash(aLoc * 0.731 + aMisc.y);
        float eyaw = aMisc.x;
        vec2 ecs = vec2(cos(eyaw), sin(eyaw));
        vec3 elocal = vec3(0.0);
        vec3 objectNormal = vec3(0.0, 1.0, 0.0);
        vec3 ecol = vec3(0.3);
        float etop = max(eh, 0.05);
        float ech = eh * uCrown;
        float efoot = max(eh - 0.5 * ech, 0.02);          // where the trunk ends and the crown is centred
        #if ECO_PART == 0
          // trunk: a tapered tube, bark by the direction round it and the height up it (frozenTrees.ts's bark shader)
          float tw = max(edia, 0.008);
          elocal = vec3(position.x * tw, position.y * efoot, position.z * tw);
          vec2 erad = position.xz;
          objectNormal = normalize(vec3(erad.x, 0.2, erad.y));
          ecol = vec3(0.30, 0.22, 0.15) * (0.75 + 0.5 * eseed);
          vec2 eradR = vec2(erad.x * ecs.x - erad.y * ecs.y, erad.x * ecs.y + erad.y * ecs.x);
          vec2 elx = vec2(elocal.x * ecs.x - elocal.z * ecs.y, elocal.x * ecs.y + elocal.z * ecs.x);
          elocal = vec3(elx.x, elocal.y, elx.y);
          objectNormal = vec3(eradR.x, objectNormal.y, eradR.y);
          vBark = 0.0;
          vBarkDir = eradR;
          vBarkV = position.y * efoot * ${BARK_V_PER_C.toFixed(2)} / (6.2831853 * max(0.5 * tw * mix(1.0, 0.55, position.y), 0.01));
        #elif ECO_PART == 1
          // crown: sprigs through the crown ellipsoid, each pointing out of it and ending inside it: a sprig starts between the middle and a third of the way out and runs to the ellipsoid's surface at most, so
          // the crown that is drawn is the plant's stated height and width (its sprigs used to run out past the ellipsoid: a shrub stood a quarter to a third taller and nearly twice as wide as the data)
          float ez = aBlade.x, eph = aBlade.y + eyaw, esr = sqrt(max(1.0 - ez * ez, 0.0));
          vec3 en = vec3(esr * cos(eph), ez, esr * sin(eph));
          float elx2 = 0.85 + 0.30 * ehash(aLoc * 0.37 + 4.0), elz2 = 0.85 + 0.30 * ehash(aLoc * 0.53 + 9.0);
          vec3 esemi = vec3(ecw * 0.5 * elx2, ech * 0.5, ecw * 0.5 * elz2);
          vec3 eanchor = vec3(0.0, efoot, 0.0) + en * esemi * (${SPRIG_ANCHOR[0].toFixed(2)} + ${SPRIG_ANCHOR[1].toFixed(2)} * aBlade.z);
          vec3 esd = normalize(en * vec3(1.0, 0.7, 1.0) + vec3(0.0, 0.3, 0.0));
          vec3 eqp = (eanchor - vec3(0.0, efoot, 0.0)) / esemi, eqd = esd / esemi;               // the sprig's axis in the ellipsoid's own unit-sphere frame: where it leaves
          float eqq = dot(eqd, eqd), eqb = dot(eqp, eqd);
          float eexit = (-eqb + sqrt(max(eqb * eqb - eqq * (dot(eqp, eqp) - 1.0), 0.0))) / max(eqq, 1e-6);
          float eRm = (esemi.x + esemi.y + esemi.z) * 0.3333;
          float esL = min(min(eRm * sqrt(18.0 / float(ECO_K)) * inversesqrt(elod), 1.15 * eRm), ${SPRIG_REACH.toFixed(2)} * eexit);
          vec3 esRef = abs(esd.y) < 0.99 ? vec3(0.0, 1.0, 0.0) : vec3(1.0, 0.0, 0.0);
          vec3 esX0 = normalize(cross(esRef, esd));
          vec3 esZ0 = cross(esd, esX0);
          float esRoll = 6.2831853 * fract(aBlade.z * 13.7 + aBlade.y * 3.1);
          vec3 esX = esX0 * cos(esRoll) + esZ0 * sin(esRoll);
          vec3 esZ = esZ0 * cos(esRoll) - esX0 * sin(esRoll);
          elocal = eanchor + esd * (position.y * esL) + (esX * position.x + esZ * position.z) * (esL * 0.95);
          vec3 ecardN = esX * normal.x + esZ * normal.z;
          objectNormal = normalize(ecardN * 0.45 + esd * 0.35 + vec3(0.0, 0.85, 0.0));
          vNeedleUv = uv * clamp(floor(esL / ${SPRAY_M.toFixed(2)}), 1.0, 8.0);      // the card's coordinates in sprays; the fragment shader repeats the spray
          vPalmUv = vec3(0.0);
          // leaves: darker low in the crown, lighter on top; young stands (low bin) lighter and yellower, a few plants redder (new ohia leaves)
          float ebin = aMisc.y / 7.0;
          vec3 leafLo = mix(vec3(0.10, 0.20, 0.07), vec3(0.18, 0.30, 0.09), 1.0 - ebin);
          vec3 leafHi = mix(vec3(0.20, 0.34, 0.11), vec3(0.32, 0.44, 0.14), 1.0 - ebin);
          float esv = 0.85 + 0.30 * fract(aBlade.z * 91.7 + eseed * 5.0);
          ecol = mix(leafLo, leafHi, smoothstep(-0.8, 0.9, en.y)) * (0.8 + 0.4 * eseed) * esv * 1.1;
          ecol = mix(ecol, vec3(0.45, 0.22, 0.12), 0.35 * step(0.93, eseed) * (1.0 - ebin));
        #else
          float et = position.y;
          #if ECO_PART == 3
            float eaz = gaz + eyaw;
          #else
            float eaz = aBlade.x + eyaw;
          #endif
          vec2 edir = vec2(cos(eaz), sin(eaz));
          vec2 eside = vec2(-edir.y, edir.x);
          float erad = aBlade.z * ecw * 0.5;
          #if ECO_PART == 3
            // grass: a tuft of leaning blades (grass.ts's colours)
            float eL = eh * aBlade.w;
            float ey = eL * et * (1.0 - 0.15 * et * aBlade.y);
            float ereach = eL * aBlade.y * et * et * 0.7;
            float ew = gw0 * gwide * (1.0 - 0.9 * et);
            vec3 tipc = mix(vec3(0.38, 0.52, 0.18), vec3(0.80, 0.74, 0.46), smoothstep(${GRASS_STRAW[0].toFixed(2)}, ${GRASS_STRAW[1].toFixed(2)}, ehash(aLoc * 0.113 + 3.0)));
            ecol = mix(vec3(0.12, 0.20, 0.06), tipc, pow(et, 0.7)) * (0.8 + 0.4 * eseed) * (0.88 + 0.24 * fract(grk * 0.618));
            objectNormal = normalize(vec3(edir.x * 0.35, 1.0, edir.y * 0.35));
            vec2 exz = edir * (erad + ereach) + eside * (position.x * ew);
            elocal = vec3(exz.x, ey, exz.y);
          #else
            // fern: a frond is a strip that arches out of the tuft; the leaflets of the sheet hang in a V either side of its rib, and narrow to the tip
            float eFl = ecw * 0.5 * aBlade.w;
            float ey = eh * (0.08 + 3.7 * et * (1.0 - et)) * aBlade.w;
            float ereach = eFl * et;
            float eW = eFl * 0.55 * mix(1.0, 0.06, smoothstep(0.70, 1.0, et)) * min(1.0, 0.25 + 4.0 * et);
            float eu = position.x;
            float ehang = abs(eu) * (0.35 + 0.65 * abs(eu)) * eW * 0.8;
            vec2 exz = edir * (erad + ereach) + eside * (eu * eW);
            elocal = vec3(exz.x, max(ey - ehang, 0.0), exz.y);
            ecol = mix(vec3(0.10, 0.22, 0.06), vec3(0.26, 0.42, 0.12), et) * (0.8 + 0.4 * eseed);
            objectNormal = normalize(vec3(edir.x * 0.3, 1.0, edir.y * 0.3));
            vPalmUv = vec3(abs(eu), et, 0.0);
            vNeedleUv = vec2(abs(eu), 0.5 + 0.5 * et);
          #endif
        #endif
        // wind: the push at the plant, taken over what stands up
        vec2 epush = windPush(erel + uGOrg);
        vec3 esway = treeSwayAt(elocal, vec2(0.0, etop), eseed * 31.0, epush);
        #if ECO_PART >= 2
          esway *= 3.0;
        #endif
        elocal += esway;
        elocal *= efade;
        vTreeCol = pow(ecol, vec3(2.2));
      `)
      .replace('#include <begin_vertex>', /* glsl */`
        vec3 transformed = vec3(0.0, -1.0e4, 0.0);
        if (evis > 0.0) transformed = vec3(erel.x, eground - 0.02, erel.y) + elocal;
      `);
    const fragDecl = part === PART_TRUNK ? BARK_FRAG_DECL
      : part === PART_CROWN || part === PART_FERN ? 'uniform sampler2D uNeedle; uniform sampler2D uPalmLeaf; uniform vec3 uPalmMean; uniform float uPalmReady; varying vec2 vNeedleUv; varying vec3 vPalmUv;' : '';
    sh.fragmentShader = sh.fragmentShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vTreeCol;\n' + fragDecl);
    if (part === PART_TRUNK) {
      sh.fragmentShader = sh.fragmentShader
        .replace('#include <color_fragment>', BARK_COLOR)
        .replace('#include <normal_fragment_maps>', `#include <normal_fragment_maps>\ngBarkDH *= ${BARK_BUMP_SCALE.toFixed(2)};\n` + BARK_NORMAL);
    } else {
      const color = part === PART_CROWN ? /* glsl */`
        vec2 nGx = dFdx(vNeedleUv) * vec2(1.0, 0.48), nGy = dFdy(vNeedleUv) * vec2(1.0, 0.48);
        vec4 needleS = textureGrad(uNeedle, vec2(fract(vNeedleUv.x), fract(vNeedleUv.y) * 0.48), nGx, nGy);   // the lower half of the atlas is the needle spray
        diffuseColor.a *= needleS.a;
        if (diffuseColor.a < mix(${ALPHA_CUT.toFixed(2)}, ${ALPHA_CUT_FAR.toFixed(2)}, clamp(log2(max(max(length(nGx), length(nGy)), 1e-6) * 128.0) / 3.0, 0.0, 1.0))) discard;
        diffuseColor.rgb *= vTreeCol * needleS.rgb * 1.35;
      ` : part === PART_FERN ? /* glsl */`
        vec4 frondS; vec3 frondMul;
        if (uPalmReady > 0.5) {
          // the real frond's colour over its mean, so the plant's own green still sets the hue
          frondS = texture2D(uPalmLeaf, vPalmUv.xy);
          frondMul = clamp(frondS.rgb / uPalmMean, 0.3, 2.2) * 1.15;
        } else {
          frondS = texture2D(uNeedle, vNeedleUv);                    // until the frond sheet has decoded: the atlas's own frond half
          frondMul = frondS.rgb * 1.35;
        }
        diffuseColor.a *= frondS.a;
        if (diffuseColor.a < mix(${ALPHA_CUT.toFixed(2)}, ${ALPHA_CUT_FAR.toFixed(2)}, clamp(log2(max(max(length(dFdx(vPalmUv.xy)), length(dFdy(vPalmUv.xy))), 1e-6) * 512.0) / 3.0, 0.0, 1.0))) discard;
        diffuseColor.rgb *= vTreeCol * frondMul;
      ` : 'diffuseColor.rgb *= vTreeCol;';
      sh.fragmentShader = sh.fragmentShader
        .replace('#include <color_fragment>', color)
        // double-sided cards would flip the normal on back faces, making half of them dark
        .replace('#include <normal_fragment_begin>', '#include <normal_fragment_begin>\nnormal = normalize(vNormal);');
    }
  };
  return m;
}

// ---- the view ----------------------------------------------------------------------------------------------------------------------------------------------------

interface Piece { mesh: THREE.Mesh; geo: THREE.InstancedBufferGeometry; mat: THREE.MeshStandardMaterial; base: THREE.Vector2; win: EcoWindow; count: number }
/** The template geometries of a band: a tree's trunk and crown, a shrub's crown, a fern, a grass tuft. */
interface Templates { trunk: THREE.BufferGeometry; crownTree: THREE.BufferGeometry; crownShrub: THREE.BufferGeometry; fern: THREE.BufferGeometry; grass: THREE.BufferGeometry }

export class EcoView {
  private readonly pieces = new Map<string, Piece[]>();
  private readonly wins = new Map<string, EcoWindow>();        // the window each id's pieces were made from
  private readonly shared: Templates[] = [];                     // by stream
  private readonly tex: MatShared;
  private seenVersion = -1;
  private seenOrigin = -1;
  private quality = 1;
  private enabled = true;
  private lastPrune = 0;
  private readonly group = new THREE.Group();

  constructor(private readonly scene: THREE.Scene, private readonly wind: WindField, private readonly clip: GroundClip, private readonly origin: Origin, private readonly store: EcoStore) {
    this.tex = { needle: makeNeedleTexture(), palm: makePalmTextures() };
    for (let s = 0; s < ECO_STREAMS.length; s++) {
      this.shared.push({
        trunk: trunkGeometry(s >= 3 ? 5 : s === 2 ? 6 : 8, 0.55),
        crownTree: crownGeometry(CROWN_SPRIGS[s]!, SPRIG_CARDS[s]!),
        crownShrub: crownGeometry(SHRUB_SPRIGS[s]!, SPRIG_CARDS[s]!),
        fern: frondGeometry(FERN_FRONDS, FERN_SEGS),
        grass: ribbonGeometry(grassBlades(), GRASS_SEGS),
      });
    }
    this.group.frustumCulled = false;
    scene.add(this.group);
  }

  /** Call once per frame with the camera's position (three frame), after origin.update. */
  update(eye: THREE.Vector3, nowMs: number = performance.now()): void {
    if (nowMs - this.lastPrune > 500) { this.lastPrune = nowMs; this.store.prune(eye.x, eye.z); }
    const ready = this.clip.top >= 0 && this.clip.hasParams;
    if (ready && this.store.version !== this.seenVersion) this.sync();
    if (ready && this.origin.version !== this.seenOrigin) this.refreshSpheres();
    const ox = this.origin.org.x, oz = this.origin.org.y;
    this.group.position.set(ox, 0, oz);
    for (const ps of this.pieces.values()) for (const p of ps) p.base.set(p.win.baseX - ox, p.win.baseZ - oz);
  }

  private sync(): void {
    this.seenVersion = this.store.version;
    for (const [id, ps] of this.pieces) {
      const w = this.store.windows.get(id);
      if (w && this.wins.get(id) === w) continue;
      for (const p of ps) this.drop(p);
      this.pieces.delete(id);
      this.wins.delete(id);
    }
    for (const [id, w] of this.store.windows) {
      if (this.pieces.has(id)) continue;
      const ps: Piece[] = [];
      const tpl = this.shared[w.meta.stream]!;
      for (let f = 0; f < 4; f++) {
        const F = w.forms[f]!;
        if (F.count === 0) continue;
        // the window's plants of this form, as instance attributes (a tree's trunk and crown share them)
        const aLoc = new THREE.InstancedBufferAttribute(F.loc, 2), aSz = new THREE.InstancedBufferAttribute(F.size, 4), aMisc = new THREE.InstancedBufferAttribute(F.misc, 2);
        const s = w.meta.stream;
        const parts: { part: number; tpl: THREE.BufferGeometry; shrub: boolean; k: number }[] =
          f === FORM_TREE ? [{ part: PART_TRUNK, tpl: tpl.trunk, shrub: false, k: 0 }, { part: PART_CROWN, tpl: tpl.crownTree, shrub: false, k: CROWN_SPRIGS[s]! }]
          : f === FORM_SHRUB ? [{ part: PART_CROWN, tpl: tpl.crownShrub, shrub: true, k: SHRUB_SPRIGS[s]! }]
          : f === FORM_FERN ? [{ part: PART_FERN, tpl: tpl.fern, shrub: false, k: 0 }]
          : [{ part: PART_GRASS, tpl: tpl.grass, shrub: false, k: 0 }];
        for (const pt of parts) {
          const geo = new THREE.InstancedBufferGeometry();
          geo.index = pt.tpl.index;
          for (const n of Object.keys(pt.tpl.attributes)) geo.setAttribute(n, pt.tpl.attributes[n]!);
          geo.setAttribute('aLoc', aLoc);
          geo.setAttribute('aSz', aSz);
          geo.setAttribute('aMisc', aMisc);
          geo.instanceCount = Math.max(1, Math.floor(F.count * this.quality));
          const base = new THREE.Vector2();
          const mat = makeMaterial(pt.part, pt.shrub, pt.k, base, this.origin.eye, this.clip, this.wind, ECO_STREAMS[s]!.radius, this.tex);
          const mesh = new THREE.Mesh(geo, mat);
          mesh.castShadow = false;
          mesh.receiveShadow = true;
          mesh.visible = this.enabled;
          this.group.add(mesh);
          ps.push({ mesh, geo, mat, base, win: w, count: F.count });
        }
      }
      this.pieces.set(id, ps);
      this.wins.set(id, w);
    }
    this.refreshSpheres();
  }

  /** The bounding spheres that let three cull a window outside the view: centred on the window (relative to O, at the ground's height there), wide enough for the ground's rise across it. */
  private refreshSpheres(): void {
    this.seenOrigin = this.origin.version;
    const ox = this.origin.org.x, oz = this.origin.org.y;
    for (const ps of this.pieces.values()) for (const p of ps) {
      const w = p.win;
      if (!Number.isFinite(w.cx)) { p.mesh.frustumCulled = false; continue; }
      let h = 0;
      try { h = this.clip.heightAt(w.cx, w.cz); } catch { h = 0; }
      const r = w.r * 1.4 + 60;
      p.geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(w.cx - ox, h, w.cz - oz), r);
      p.mesh.frustumCulled = true;
    }
  }

  /** Draw this share (0 to 1) of every window's plants: an even thinning. */
  setQuality(q: number): void {
    this.quality = Math.max(0.02, Math.min(1, q));
    for (const ps of this.pieces.values()) for (const p of ps) p.geo.instanceCount = Math.max(1, Math.floor(p.count * this.quality));
  }

  setEnabled(e: boolean): void {
    this.enabled = e;
    for (const ps of this.pieces.values()) for (const p of ps) p.mesh.visible = e;
  }

  /** counts for the overlay and the tests */
  get stats(): { windows: number; meshes: number; plants: number; drawn: number } {
    let meshes = 0, drawn = 0;
    for (const ps of this.pieces.values()) for (const p of ps) { meshes++; drawn += p.geo.instanceCount; }
    return { windows: this.pieces.size, meshes, plants: this.store.plants, drawn };
  }

  private drop(p: Piece): void {
    this.group.remove(p.mesh);
    p.geo.dispose();
    p.mat.dispose();
  }

  dispose(): void {
    for (const ps of this.pieces.values()) for (const p of ps) this.drop(p);
    this.pieces.clear();
    this.wins.clear();
    this.scene.remove(this.group);
    for (const t of this.shared) for (const g of Object.values(t)) g.dispose();
    this.tex.needle.dispose();
    this.tex.palm.uPalmLeaf.value?.dispose();
    this.tex.palm.uBark.value?.dispose();
  }
}

export { FORM_TREE, FORM_SHRUB, FORM_FERN, FORM_GRASS };
